import {
  bySubstance,
  hero,
  heroCss,
  joinLabels,
  pageLabel,
  panel,
  panelCss,
  rankMarker,
  rankMarkerCss,
  readingTime,
} from '../armKit';
import { cardPartCss, collapsedRowCss, compactCardsCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;
type Lead = { card: ProfileCard; url: string };

const FLAG = 'fulldose';
const HERO = 'hero';
const FULL = 'full';
const COLLAPSED = 'collapsed';
// The hero counts as the first of the five works this arm opens, so the
// reading window stays the size qualified_abstracts opens rather than
// growing by one.
const EXPANDED_COUNT = 4;
const LABEL = 'Start with this paper';
const HERO_ACTION = 'Read the paper';
const READ_ACTION = 'Read';
const LEAD_SOURCE = 'profile-liautotest-fulldose-lead';
const READ_SOURCE = 'profile-liautotest-fulldose-read';

function pagesOf(facts: Facts, workId: number): number | null | undefined {
  return facts.get(workId)?.pageCount;
}

function flagOf(card: ProfileCard): string | null {
  return card.flag(FLAG);
}

// The same choice lead_paper_and_index makes, so the two arms differ in dose
// rather than in which paper they lead with.
function leadOf(cards: ProfileCard[]): Lead | null {
  const card = cards.find(
    (candidate) => candidate.hasHiddenAbstract && candidate.url != null,
  );

  return card?.url == null ? null : { card, url: card.url };
}

function heroFor(
  { card, url }: Lead,
  facts: Facts,
  ctx: ArmContext,
): HTMLElement {
  const { root, body } = panel({ title: LABEL });

  const pages = pagesOf(facts, card.workId);
  const meta = joinLabels([pageLabel(pages), readingTime(pages)]);
  const track = (): void => ctx.track(LEAD_SOURCE, { entityId: card.workId });

  hero(body, {
    title: card.title,
    href: url,
    onTitleClick: track,
    meta: meta === '' ? undefined : meta,
    abstract: card.abstractText(),
    actions: [{ label: HERO_ACTION, href: url, onClick: track }],
  });

  return root;
}

// A card with nothing hidden still belongs to the opened run: it keeps its
// chip and its action, it simply has less to reveal.
function expand(
  card: ProfileCard,
  facts: Facts,
  rank: number,
  ctx: ArmContext,
): void {
  card.setFlag(FLAG, FULL);
  card.expandAbstract();
  card.prependToTitle(rankMarker(`${rank}.`));

  const pages = pageLabel(pagesOf(facts, card.workId));
  if (pages != null) card.addChip(pages);
  if (card.url == null) return;

  const action = card.addAction(READ_ACTION, card.url);
  action?.addEventListener('click', () => {
    ctx.track(READ_SOURCE, { entityId: card.workId });
  });
}

// Ranks are the work's position in the whole corpus, hero included, so the
// rows below the opened run carry on from it rather than restarting at one.
function indexRow(
  card: ProfileCard,
  facts: Facts,
  rank: number,
  ctx: ArmContext,
): void {
  card.collapse();
  const rowScope = `${ctx.scope} ${card.setFlag(FLAG, COLLAPSED)}`;
  ctx.injectStyles(
    compactCardsCss(rowScope, { itemGap: '8px' }) +
      collapsedRowCss(rowScope) +
      cardPartCss('row', rowScope, 'align-items: center;') +
      cardPartCss('body', rowScope, 'margin-bottom: 0 !important;') +
      cardPartCss('title', rowScope, 'margin-bottom: 0 !important;'),
  );

  card.prependToTitle(rankMarker(`${rank}.`));

  const pages = pageLabel(pagesOf(facts, card.workId));
  if (pages != null) card.addChip(pages);
}

export const profileFullDose: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    const compare = bySubstance((workId) => pagesOf(facts, workId));
    const lead = leadOf([...ctx.cards()].sort(compare));
    if (lead == null) return;

    // Last gate before the first mutation: a profile with no list cannot
    // carry the hero, and half the arm is worse than control.
    const slot = ctx.mountSlot('listHero');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope) +
        heroCss(ctx.scope) +
        rankMarkerCss(ctx.scope, {
          minWidth: '32px',
          fontSize: '20px',
          marginRight: '6px',
          narrowMinWidth: '26px',
        }),
    );
    ctx.reorderCards(compare, 'list');

    // Flagged and hidden, so the lead degrades to no lead at all rather than
    // a second copy of the hero if the arm's stylesheet is lost.
    lead.card.setFlag(FLAG, HERO);
    lead.card.hide();
    slot.appendChild(heroFor(lead, facts, ctx));

    // Read back rather than reuse the sorted array: the reorder is what
    // decided the run, and the DOM is the only record of it.
    ctx
      .cards()
      .filter((card) => flagOf(card) !== HERO)
      .forEach((card, offset) => {
        if (offset < EXPANDED_COUNT) expand(card, facts, offset + 2, ctx);
        else indexRow(card, facts, offset + 2, ctx);
      });
  },

  // Gated on the hero the first pass left rather than on a fresh read, so a
  // late batch cannot engage an arm that degraded to control. Collapsed and
  // numbered on: no reorder, no second hero, and nothing new opened under a
  // reader who is already scrolling.
  onCards: async (cards, ctx) => {
    const facts = await ctx.workFacts();
    const all = ctx.cards();
    if (!all.some((card) => flagOf(card) === HERO)) return;

    const from = all.filter((card) => flagOf(card) != null).length + 1;
    cards
      .filter((card) => flagOf(card) == null)
      .forEach((card, offset) => indexRow(card, facts, from + offset, ctx));
  },
};
